import _ from "lodash";
import React from "react";
import { DocumentDetailsCard } from "../../../utilities/DocumentDetailsCard";
import { DocumentUploadSignature } from "../../../../types/DocumentType";
import { Controller, useFormContext } from "react-hook-form";
import { FormInput, FormSelect } from "../../../forms";
import { BooleanSelect, ClientAccountSelect, DocumentFunctionsSelect, DocumentTypesSelect } from "../../../forms/selects";
import { ClientProjectSearchSelect } from "../../../forms/selects/ClientProjectSearchSelect";
import { ThemedIcon } from "../../../utilities";
import { niceBytes } from "../../../../helpers/fileHelpers";
import { useFileNameChecker } from "../hooks/useFileNameChecker";

export const FileDetailsForm = ({ fileIcon, isFileNameValid, setIsFileNameValid, userId } : Readonly<{ fileIcon: string, isFileNameValid: boolean, setIsFileNameValid: (v: boolean) => void, userId: string }>) => {
    const [isFileNameValidOverride, setIsFileNameValidOverride] = React.useState<boolean>(false);
    const { checkFileName } = useFileNameChecker();
    const { control, formState, setValue, watch } = useFormContext();
    const { errors } = formState;
    const fileData = watch('fileData');
    const fileName = watch('fileName');
    const adviserId = watch('adviserId');
    const divisionId = watch('divisionId');
    const documentType = watch('documentType');
    const masterAccountId = watch('masterAccountId');
    const isClientVisible = watch('isClientVisible');
    const isSignaturesRequired = watch('isClientSignaturesRequired');
    const requiredSignatures : DocumentUploadSignature[] = watch('requiredSignatures');

    const onClearFiles = () => {
        setValue('fileData', null, { shouldDirty: true });
    };

    const onFileNameInputBlur = async (value: string) => {
        if (_.isEqual(value, fileName)) {
            return;
        }
        const isFileNameValid = await checkFileName(value, masterAccountId);
        setIsFileNameValid(isFileNameValid);
    };

    if (!fileData) {
        return null;
    };

    return (
        <React.Fragment>
            <DocumentDetailsCard 
                clearFile={onClearFiles}
                fileName={fileData.name}
                fileSize={fileData.size}
                type={documentType}
            />
            {masterAccountId && (
                <div className="row mb-3">
                    <div className="col-12">
                        <Controller
                            name="projectId"
                            control={control}
                            render={({ field: { ref, value, onChange, ...rest } }) => (
                                <ClientProjectSearchSelect
                                    adviserId={adviserId}
                                    divisionId={divisionId}
                                    masterAccountId={masterAccountId!}
                                    userId={userId}
                                    value={value}
                                    onChange={(e: any) => {
                                        if (e === null) {
                                            setValue('projectId', null);
                                            return;
                                        }
                                        setValue('projectId', e.value)
                                    }}
                                />
                            )}
                        />
                    </div>
                </div>
            )}
            <div className="row mb-3">
                <div className="col">
                    <Controller
                        name="fileName"
                        control={control}
                        rules={{ 
                            required: 'A file name must be given.',
                            maxLength: {
                                value: 1024,
                                message: 'File name cannot be more than 1024 characters.'
                            } 
                        }}
                        render={({ field: { ref, onBlur, ...rest } }) => (
                            <FormInput 
                                label="File Name" 
                                disableAnimations={true} 
                                errorAllowRetry={false}
                                errorMessage={errors?.fileName?.message}
                                onBlur={onFileNameInputBlur}
                                {...rest} 
                            />
                        )}
                    />
                </div>
            </div>
            {isFileNameValid === false && (
                <div className="d-flex flex-column p-3 mb-3 border border-2 border-warning border-opacity-10">
                    <p className="fs-6">The file name you have set already exists for this client. You will have to choose to either rename the file or replace the existing file with this one.</p>
                    <Controller
                        name="conflictBehaviour"
                        control={control}
                        rules={{ 
                            required: 'You must select a valid conflict behavior.',
                        }}
                        render={({ field: { ref, value, onChange, ...rest } }) => (
                            <FormSelect 
                                label={null}
                                className="mb-2"
                                disableAnimations={true} 
                                errorAllowRetry={false}
                                errorMessage={errors?.conflictBehaviour?.message}
                                defaultValue={value}
                                options={[
                                    { label: 'Auto Rename', value: 2 },
                                    { label: 'Replace Existing', value: 1 },
                                ]}
                                onChange={(values) => {
                                    setValue('conflictBehaviour', values.value)
                                    setIsFileNameValidOverride(values.value != null);
                                }}
                                {...rest} 
                            />
                        )}
                    />
                </div>
            )}
            <div className="row mb-3">
                <div className="col">
                    <Controller
                        name="documentDescription"
                        control={control}
                        rules={{ 
                            maxLength: {
                                value: 2048,
                                message: 'Document description cannot be more than 2048 characters.'
                            } 
                        }}
                        render={({ field: { ref, ...rest } }) => (
                            <FormInput 
                                label="Document Description" 
                                disableAnimations={true} 
                                errorAllowRetry={false}
                                errorMessage={errors?.documentDescription?.message}
                                {...rest} 
                            />
                        )}
                    />
                </div>
            </div>
            <div className="row mb-3">
                <div className="col-3">
                    <Controller
                        name="documentType"
                        control={control}
                        rules={{ required: "A Document Type must be selected." }}
                        render={({ field: { ref, value, onChange, ...rest } }) => (
                            <DocumentTypesSelect
                                label="Document Type"
                                isDisabled={true}
                                defaultValue={value}
                                errorMessage={errors?.documentType?.message} 
                                errorAllowRetry={false}
                                {...rest}
                            />
                        )}
                    />
                </div>
                <div className="col">
                    <Controller
                        name="documentFunctionId"
                        control={control}
                        rules={{ required: "A Document Function must be selected." }}
                        render={({ field: { ref, value, onChange, ...rest } }) => (
                            <DocumentFunctionsSelect
                                label="Document Function"
                                defaultValue={value}
                                errorMessage={errors?.documentFunctionId?.message} 
                                errorAllowRetry={false}
                                onChange={(values) => setValue('documentFunctionId', values.value)}
                                {...rest}
                            />
                        )}
                    />
                </div>
            </div>
            {masterAccountId && (
                <>
                    <div className="row mb-3">
                        <div className="col-6">
                            <Controller
                                name="isClientVisible"
                                control={control}
                                render={({ field: { ref, value, onChange, ...rest } }) => (
                                    <BooleanSelect
                                        label="Client Visible"
                                        defaultValue={value}
                                        disableAnimations={true}
                                        onChange={(values: any) => {
                                            setValue('isClientVisible', values.value)

                                            // when we make the client visibility false, then reset the other values
                                            if (values.value === false) {
                                                setValue('isClientSignaturesRequired', false);
                                                setValue('requiredSignatures', []);
                                            }
                                        }}
                                        {...rest}
                                    />
                                )}
                            />
                        </div>
                        <div className="col-6">
                             <Controller
                                name="isClientSignaturesRequired"
                                control={control}
                                render={({ field: { ref, value, onChange, ...rest } }) => (
                                    <BooleanSelect
                                        label="Client Signature Required"
                                        defaultValue={value}
                                        isDisabled={!isClientVisible}
                                        disableAnimations={true}
                                        onChange={(values: any) => {
                                            setValue('isClientSignaturesRequired', values.value)

                                            // when we turn off the signatures, reset the required signatures
                                            if (values.value === false) {
                                                setValue('requiredSignatures', []);
                                            }
                                        }}
                                        {...rest}
                                    />
                                )}
                            />
                        </div>
                    </div>
                    {isSignaturesRequired && (
                        <div className="row mb-3">
                            <div className="col">
                                 <Controller
                                    name="requiredSignatures"
                                    control={control}
                                    render={({ field: { ref, value, onChange, ...rest } }) => (
                                        <ClientAccountSelect
                                            label="Signatures Required"
                                            masterAccountId={masterAccountId}
                                            defaultValue={value.map((el: DocumentUploadSignature) => el.clientAccountId)}
                                            isMulti={true}
                                            isDisabled={!isClientVisible || !isSignaturesRequired}
                                            disableAnimations={true}
                                            onChange={(values: any) => {
                                                const newValues : DocumentUploadSignature[] = values.map((el: any) => ({
                                                    clientAccountId: el.value,
                                                    authorisationCode: requiredSignatures.find((i: DocumentUploadSignature) => i.clientAccountId === el.value)?.authorisationCode ?? null
                                                } as DocumentUploadSignature));
                                                setValue('requiredSignatures', newValues);
                                            }}
                                            {...rest}
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    )}
                </>
            )}
        </React.Fragment>
    )
};


/*
<div className="card">
                    <div className="card-body">
                        <p>
                            <ThemedSpan variant="primary" className="fs-5">Client Options</ThemedSpan>
                        </p>
                        <div className="d-flex flex-row-reverse">
                            <Controller 
                                name="isClientVisible"
                                control={control}
                                render={({ field: { ref, value, onChange, ...rest } }) => (
                                    <FormCheck
                                        type="checkbox"
                                        label="Client Readable"
                                        disableAnimations={true}
                                        isChecked={value}
                                        onChange={(isChecked: boolean) => setValue('isClientVisible', isChecked, { shouldDirty: true })}
                                        {...rest}
                                    />
                                )}
                            />
                        </div>
                    </div>
                </div>


{allowProjectSelect === true && (
                <div className="row mb-3">
                    <div className="col">
                        <Controller
                            name="projectId"
                            control={control}
                            render={({ field: { ref, value, onChange, ...rest } }) => (
                                <ClientProjectSearch
                                    buttonSize="sm"
                                    label="Project"
                                    labelPosition="top"
                                    formError={errors?.projectId?.message} 
                                    value={value}
                                    onProjectCreated={(project) => setValue('projectId', project.projectId)}
                                    onProjectSelected={(_, project) => setValue('projectId', project.projectId)}
                                    {...rest}
                                />
                            )}
                        />
                    </div>
                </div>
            )}
*/